import Spinner from '../ui/Spinner';

/**
 * The submit control: a fixed bar showing the total on phones, an inline button on desktop.
 *
 * THE SUBMIT BUTTON IS NEVER DISABLED. It previously carried `disabled` whenever the form was
 * incomplete, which reads as a helpful guard and is in fact a keyboard trap: a disabled button
 * is removed from the tab order entirely, so a keyboard or screen reader user tabs to the end
 * of the form, finds nothing, and gets no explanation. The most important control on a payment
 * page was unreachable to exactly the people most likely to need it announced.
 *
 * Enabled-and-rejecting is both more accessible and more honest. Pressing submit with an
 * incomplete form runs validation, renders the blockers, and moves focus to the first offending
 * field -- an action with a result, instead of a control that silently does nothing.
 *
 * `submitting` is the one genuine disable: it prevents a double charge, and it is a transient
 * state the user just caused, not a standing barrier.
 *
 * THE BLOCKER LIST IS NO LONGER HERE. It moved to OrderSummary, where the redesign draws it,
 * and this component receives its `blockerListId` so `aria-describedby` still resolves. That
 * wiring is the reason the id is a prop: a screen reader user must hear WHY the form will
 * reject when focus reaches this button, and the reasons now live in a different component.
 *
 * @param {object} props
 * @param {number} props.blockerCount     how many things are outstanding; styling only
 * @param {string} [props.blockerListId]  id of OrderSummary's blocker panel
 * @param {boolean} props.submitting
 * @param {string} [props.submitError]
 * @param {number} props.dueToday         total due today, for the fixed bar's readout
 * @param {boolean} [props.fixedOnMobile] pin to the viewport bottom below `lg` (default true)
 */
export default function SubmitBar({
  blockerCount = 0,
  blockerListId,
  submitting,
  submitError,
  dueToday,
  fixedOnMobile = true,
}) {
  const hasBlockers = blockerCount > 0;

  const button = (
    <button
      id="checkout-and-portal-button"
      type="submit"
      // ONLY while submitting -- see the note above about the tab-order trap.
      disabled={submitting}
      // Points at the blocker list in OrderSummary so a screen reader user hears WHY the form
      // will reject when focus reaches the button, rather than discovering it by pressing.
      aria-describedby={hasBlockers && !submitting ? blockerListId : undefined}
      // Auto width on the phone bar, where it sits beside the total; full width on desktop,
      // where it is the last thing in the summary column.
      className={`inline-flex min-h-[48px] w-auto items-center justify-center rounded-sm px-6 text-sm font-semibold text-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 lg:w-full ${
        submitting
          ? 'cursor-wait bg-gray-400'
          : hasBlockers
            ? // Muted, but still focusable, clickable, and announced. The styling says "not
              // ready yet"; the behavior still explains why when pressed.
              'bg-primary-500 hover:bg-primary-600'
            : 'bg-primary-600 hover:bg-primary-700'
      }`}
    >
      {submitting ? (
        <>
          <Spinner className="-ml-1 mr-2 h-5 w-5 text-white" />
          Processing...
        </>
      ) : (
        'Continue to payment'
      )}
    </button>
  );

  /*
    A failed submit renders here instead of in alert(). An alert cannot be styled or dismissed
    by keyboard and blocks the page; role="alert" announces this without stealing focus, leaving
    the customer where they were.
  */
  const error = submitError && (
    <div
      className="border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm text-danger-700"
      role="alert"
    >
      {submitError}
    </div>
  );

  return (
    <div className="space-y-3">
      {/*
        ONE error node, in the document flow, never duplicated per breakpoint. Rendering a
        mobile copy and a desktop copy would put two role="alert" elements with the same text in
        the page, and a screen reader announces both -- the customer hears the decline twice.

        In flow rather than inside the fixed bar, too: a submit failure can run to several lines,
        and growing a pinned bar upward would cover the very fields that need correcting.
      */}
      {error}

      {/*
        Fixed below `sm` ONLY, per the handoff's responsive spec: phones get a pinned bar, and
        the 640-1023 band is a single column with the summary and submit inline at the end of
        the scroll. Pinning it up to `lg` was tempting -- a 768px tablet is a long scroll to
        reach the total -- but that is a design decision to make deliberately, not by leaving a
        breakpoint at the value that happened to match the two-column layout.

        DynamicForm adds matching bottom padding to the form so this can never cover the last
        field -- a fixed bar without that padding hides whatever it overlaps, and on this page
        that would be the terms checkbox.
      */}
      <div
        className={
          fixedOnMobile
            ? 'fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 bg-gray-900 px-4 pb-3.5 pt-3 sm:static sm:z-auto sm:bg-transparent sm:p-0'
            : 'flex items-center gap-3'
        }
      >
        {fixedOnMobile && (
          <div className="flex-1 sm:hidden">
            <div className="text-chip font-semibold uppercase text-gray-400">Due today</div>
            <div className="text-xl font-semibold tabular-nums text-white">
              ${dueToday.toFixed(2)}
            </div>
          </div>
        )}

        <div className="flex-shrink-0 lg:w-full">{button}</div>
      </div>
    </div>
  );
}
